import {
  AUTH_ACTIONS_CLASS,
  AUTH_CONTAINER_CLASS,
  AUTH_FIELDS_CLASS,
  AUTH_LINKS_ROW_CLASS,
  AUTH_LINK_CLASS,
  AuthPage,
  AuthPrimaryButton,
  AuthTextField,
  PasswordRequirements,
} from "@/components/auth";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormRootError } from "../../components/forms";
import { Checkbox, RouteLink } from "../../components/ui";

import { investorVettingService } from "@/api/investorVetting";
import { useSignupForm } from "@/hooks";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { ReactHookFormDevelopmentTools } from "../../components/developmentTools";

type TSignupProps = {
  /** Investor invitation token (006/RD-4): prefills the form, locks the email. */
  inviteToken?: string;
};

type TInviteState = "none" | "loading" | "valid" | "invalid";

export const Signup = ({ inviteToken }: TSignupProps = {}) => {
  const {
    form,
    form: {
      formState: { isSubmitting },
      control,
    },
    handleSubmit,
    legalDocumentsLoading,
  } = useSignupForm({
    defaultValues: { firstName: "", lastName: "", email: "", phone: "", password: "", confirmPassword: "" },
    inviteToken,
  });

  const [inviteState, setInviteState] = useState<TInviteState>(inviteToken ? "loading" : "none");

  useEffect(() => {
    if (!inviteToken) {
      setInviteState("none");
      return;
    }
    let cancelled = false;
    setInviteState("loading");
    // Read-only lookup for prefill — never a mutation on mount (email scanners).
    void (async () => {
      const { data, error } = await investorVettingService.getInviteByToken(inviteToken);
      if (cancelled) return;
      if (error || !data) {
        setInviteState("invalid");
        return;
      }
      form.reset({
        firstName: data.data.first_name,
        lastName: data.data.last_name,
        email: data.data.email,
        phone: "",
        password: "",
        confirmPassword: "",
        acceptTerms: false,
      });
      setInviteState("valid");
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteToken, form]);

  const isInvited = inviteState === "valid";

  return (
    <Form {...form}>
      <div className={AUTH_CONTAINER_CLASS}>
        <form
          className="grid gap-6"
          onSubmit={handleSubmit}
        >
          <AuthPage
            title="Create an account"
            subtitle="Join Fundy to streamline your fundraising process, discover investment opportunities, and connect with Kenya's startup ecosystem. Get started today and take your venture to the next level."
          >
            <div className={AUTH_FIELDS_CLASS}>
              <FormRootError />

              {inviteState === "loading" && (
                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking your invitation…
                </div>
              )}
              {inviteState === "invalid" && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  This invitation has expired — please contact the person who invited you for a new one. You can also
                  register normally below.
                </div>
              )}
              {isInvited && (
                <div className="rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm text-primary">
                  You've been invited to join as an investor. Your email is pre-filled and can't be changed.
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <AuthTextField
                  control={form.control}
                  name="firstName"
                  label="First name"
                  inputProps={{ type: "text", placeholder: "Enter your first name" }}
                />
                <AuthTextField
                  control={form.control}
                  name="lastName"
                  label="Last name"
                  inputProps={{ type: "text", placeholder: "Enter your last name" }}
                />
              </div>

              <AuthTextField
                control={form.control}
                name="email"
                label="Email"
                inputProps={{
                  type: "email",
                  placeholder: "Enter your email address",
                  // RD-4: the invited email is fixed (also enforced server-side).
                  readOnly: isInvited,
                  className: isInvited ? "opacity-70" : undefined,
                }}
              />

              <AuthTextField
                control={form.control}
                name="phone"
                label="Phone"
                inputProps={{ type: "tel", placeholder: "Enter your phone number" }}
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <AuthTextField
                  control={form.control}
                  name="password"
                  label="Password"
                  inputProps={{ type: "password", placeholder: "Enter your password" }}
                />
                <AuthTextField
                  control={form.control}
                  name="confirmPassword"
                  label="Confirm Password"
                  inputProps={{ type: "password", placeholder: "Re-enter your password" }}
                />
              </div>

              <PasswordRequirements />

              {/* acceptTerms */}
              <FormField
                control={form.control}
                name="acceptTerms"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        aria-label="By registering, you agree to our Terms & Conditions and Privacy Policy"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-medium text-foreground">
                        By registering, you agree to our{" "}
                        <RouteLink
                          to="/terms"
                          className="text-primary underline"
                          target="_blank"
                        >
                          Terms & Conditions
                        </RouteLink>{" "}
                        and{" "}
                        <RouteLink
                          to="/privacy"
                          className="text-primary underline"
                          target="_blank"
                        >
                          Privacy Policy
                        </RouteLink>
                        .
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <div className={AUTH_ACTIONS_CLASS}>
                <AuthPrimaryButton
                  disabled={isSubmitting || legalDocumentsLoading}
                  isLoading={isSubmitting}
                >
                  Register
                </AuthPrimaryButton>
              </div>

              <div className={AUTH_LINKS_ROW_CLASS}>
                <RouteLink
                  to="/auth/login"
                  className={AUTH_LINK_CLASS}
                >
                  Login
                </RouteLink>
                {" | "}
                <RouteLink
                  to="/auth/forgotPassword"
                  className={AUTH_LINK_CLASS}
                >
                  Forgot my password
                </RouteLink>
              </div>
            </div>
          </AuthPage>
        </form>

        <ReactHookFormDevelopmentTools
          // @ts-expect-error - FIXME:
          control={control}
          placement="top-left"
        />
      </div>
    </Form>
  );
};
